import Axios from "axios";
import qs from 'qs';
import SnackbarUtils from './snackbarUtils';

const axiosInstance = Axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  withCredentials: true,
  paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets', encode: false }),
});

const _resStatus = (statusCode) => {
    switch (statusCode) {
      case 999:
        throw new Error("All good, just test.");
      case 200:
        throw new Error("All Good");
      case 469:
        throw new Error("Nesprávny registračný kľúč");
      case 470:
        throw new Error("Relácia na ukončenie sa nenašla");
      case 471:
        throw new Error("Používateľ nie je v databáze");
      case 472:
        throw new Error("Používateľ s týmto e-mailom už existuje");
      case 473:
        throw new Error("Obnovovací token vypršal alebo je neplatný");
      case 474:
        throw new Error("Prístupový token vypršal alebo je neplatný");
      case 475:
        throw new Error("Nepodarilo sa vytvoriť reláciu");
      case 476:
        throw new Error("Nepodarilo sa podpísať obnovovací token");
      case 477:
        throw new Error("Nepodarilo sa podpísať prístupový token");
      case 478:
        throw new Error("E-mail nebol nájdený");
      case 479:
        throw new Error("Nesprávne heslo");
      case 480:
        throw new Error("Database error 480");
      case 481:
        throw new Error("Database error 481");
      case 482:
        throw new Error("Database error 482");
      case 483:
        throw new Error("Database error 483");
      case 484:
        throw new Error("Database error 484");
      case 485:
        throw new Error("Database error 485");
      case 486:
        throw new Error("Database error 486");
      case 487:
        throw new Error("Database error 487");
      case 488:
        throw new Error("Database error 488");
      case 489:
        throw new Error("Database error 489");
      case 500:
        throw new Error("Interná chyba servera");
      default:
        throw new Error("Interná chyba servera");
    };
};

const _refreshToken = async () => {
    try {
        const refreshResponse = await axiosInstance.get('/refreshToken');

        // If refreshToken is invalid or expired -> deauth
        if (refreshResponse.status === 273) {
            //console.log("Token cannot be refreshed");

            await Axios.post(`${process.env.REACT_APP_API_URL}/logout`, null, {
              withCredentials: true,
              paramsSerializer: params => qs.stringify(params, { arrayFormat: 'brackets', encode: false }),
            });

            if (JSON.parse(localStorage.authenticated)) {
                localStorage.authenticated = "false";

                SnackbarUtils.warning("Boli ste odhlásený", {
                    autoHideDuration: 4000,
                });
            };

            return false;
        };

        SnackbarUtils.success("Automatické prihásenie", { autoHideDuration: 3000 });

        return true;

    } catch (error) {
        //console.log("Error refreshing token:", error);
        return false;
    };
};

axiosInstance.interceptors.response.use(
    async response => {
        if (response.status === 274) {
            // Token expired, try to refresh token
            const originalRequest = response.config;

            // Prevent infinite loops
            if (!originalRequest._retry) {
                originalRequest._retry = true;
                const tokenRefreshed = await _refreshToken();

                if (tokenRefreshed) {
                    // Retry the original request with the new token
                    return axiosInstance(originalRequest);
                } else {
                    // Refresh token is invalid, handle unauthenticated state
                    return Promise.reject(new Error("Unable to refresh token"));
                };
            };
        };

        // Return the response if status is not 274
        return response;
    },
    error => {
        // Handle other errors
        return Promise.reject(error);
    }
);

const POST = async (path, data, params = {}, progress) => {
    try {
        const response = await axiosInstance.post(`/${path}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: params,
          onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                  const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                  if (progress) progress(percentage);
              };
          },
        });

        return response.data;

    } catch (error) {
        _resStatus(error.response?.status || 500);
        if (error.code === "ERR_BAD_REQUEST") throw new Error("Chyba pripojenia na server");
        throw error;
    };
};

const ePOST = async (path, data, params = {}, progress) => {
  try {
      const response = await axiosInstance.post(`/${path}`, data, { params });

      return response.data;

  } catch (error) {
      _resStatus(error.response?.status || 500);
      if (error.code === "ERR_BAD_REQUEST") throw new Error("Chyba pripojenia na server");
      throw error;
  };
};

const GET = async (path, params = {}) => {
    try {
        const response = await axiosInstance.get(`/${path}`, { params });

        return response.data;

    } catch (error) {
        _resStatus(error.response?.status || 500);
        if (error.code === "ERR_BAD_REQUEST") throw new Error("Chyba pripojenia na server");
        throw error;
    };
};

const PUT = async (path, data, params = {}, progress) => {
    try {
        const response = await axiosInstance.put(`/${path}`, data, {
          headers: { 'Content-Type': 'multipart/form-data' },
          params: params,
          onUploadProgress: (progressEvent) => {
              if (progressEvent.total) {
                  const percentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                  if (progress) progress(percentage);
              };
          },
        });

        return response.data;

    } catch (error) {
        _resStatus(error.response?.status || 500);
        if (error.code === "ERR_BAD_REQUEST") throw new Error("Chyba pripojenia na server");
        throw error;
    };
};

const ePUT = async ({ path, data = {}, params = {} }) => {
  try {
    const response = await axiosInstance.put(`/${path}`, data, { params });

    return response.data;

  } catch (error) {
    _resStatus(error.response?.status || 500);
    if (error.code === "ERR_BAD_REQUEST") throw new Error("Chyba pripojenia na server");
    throw error;
  };
};

const DELETE = async ({ path, params = {} }) => {
  try {
    const response = await axiosInstance.delete(`/${path}`, { params });

    return response.data;

  } catch (error) {
    //console.log("ERROR: ", error);
    _resStatus(error.response?.status || 500);
    if (error.code === "ERR_BAD_REQUEST") throw new Error("Chyba pripojenia na server");
    throw error;
  };
};

export { GET, ePOST, POST, PUT, ePUT, DELETE };